import React, { useCallback, useMemo, useState } from "react";
import inventory from "../dev/twentytwentysixColorInventory.generated.json";
import "./TwentytwentysixColorInventoryPage.css";

function hexLuminance(hex) {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return 0.5;
  let h = hex.slice(1);
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length < 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  if ([r, g, b].some((x) => Number.isNaN(x))) return 0.5;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Dev palette view: colors inventoried from `frontend/src/components/twentytwentysix` only.
 * Regenerate data: `npm run inventory:colors` (from `frontend/`).
 */
export default function TwentytwentysixColorInventoryPage() {
  const [toast, setToast] = useState(null);

  const entries = useMemo(() => {
    const list = inventory.entries || [];
    return [...list].sort((a, b) => {
      if (a.category !== b.category) return a.category === "variable" ? -1 : 1;
      return (a.label || "").localeCompare(b.label || "");
    });
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1600);
  }, []);

  const copyText = useCallback(
    async (text, msg) => {
      try {
        await navigator.clipboard.writeText(text);
        showToast(msg || "Copied");
      } catch {
        showToast("Copy failed");
      }
    },
    [showToast]
  );

  return (
    <div className="t26-color-inv">
      <header className="t26-color-inv__header">
        <h1 className="t26-color-inv__title">Twentytwentysix color inventory</h1>
        <p className="t26-color-inv__meta">
          Colors extracted only from <code>src/components/twentytwentysix</code> (CSS/JS/TS). Generated{" "}
          <strong>{inventory.generatedAt}</strong> — <strong>{inventory.entryCount}</strong> entries from{" "}
          <strong>{inventory.fileCount}</strong> files. Refresh data:{" "}
          <code>npm run inventory:colors</code> in <code>frontend/</code>.
        </p>
      </header>

      <div className="t26-color-inv__grid">
        {entries.map((entry) => {
          const bg =
            entry.resolvedForSwatch ||
            entry.swatchFallback ||
            entry.displayValue ||
            "#ccc";
          const lum = hexLuminance(entry.resolvedForSwatch);
          const lightSwatch = entry.resolvedForSwatch && lum > 0.82;

          const copyPayload = [entry.label, entry.varName, entry.displayValue, entry.resolvedForSwatch]
            .filter(Boolean)
            .join("\n");

          return (
            <article key={entry.id} className="t26-color-inv__card">
              <button
                type="button"
                className={`t26-color-inv__swatch ${lightSwatch ? "t26-color-inv__swatch--light" : ""}`}
                style={{ background: bg }}
                title="Click to copy details"
                onClick={() => copyText(copyPayload, "Copied color info")}
                aria-label={`Copy ${entry.label}`}
              />
              <div className="t26-color-inv__body">
                <p className="t26-color-inv__label">{entry.label}</p>
                {entry.varName ? <p className="t26-color-inv__var">{entry.varName}</p> : null}
                <p className="t26-color-inv__value">{entry.displayValue}</p>
                {entry.resolvedForSwatch && entry.resolvedForSwatch !== entry.displayValue ? (
                  <p className="t26-color-inv__value">→ {entry.resolvedForSwatch}</p>
                ) : null}
                {entry.sources && entry.sources.length > 0 ? (
                  <ul className="t26-color-inv__sources">
                    {entry.sources.slice(0, 4).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                    {entry.sources.length > 4 ? <li>… +{entry.sources.length - 4} more</li> : null}
                  </ul>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      {toast ? <div className="t26-color-inv__toast" role="status">{toast}</div> : null}
    </div>
  );
}
